<template>
    <CqModal
        :showModal="show"
        :preventClosing="isSaving"
        @closeModal="closeModal"
        heading="Verlofperiode invoeren"
    >
        <InfoGrid>
            <InfoHeader>Werknemer</InfoHeader><span>{{ resourceDisplayName }}</span>
            <InfoHeader>Begindatum</InfoHeader>
            <CustomDateSelect
                label="Begindatum"
                v-model="beginDate"
                :readonly="isSaving"
                :min="minBeginDate"
                :max="endDate"
            />
            <InfoHeader>Einddatum</InfoHeader>
            <CustomDateSelect
                label="Einddatum"
                v-model="endDate"
                :readonly="isSaving"
                :min="beginDate"
            />
            <InfoHeader>Actie</InfoHeader>
            <div>
                <CqRadiobutton v-model="leavePeriodTypeRadio" :disabled="isSaving" class="radio" :value="leavePeriodTypeEnum.DeploymentsSetToLeave" label="Zet diensten op verlof" />
                <CqRadiobutton v-model="leavePeriodTypeRadio" :disabled="isSaving" class="radio" :value="leavePeriodTypeEnum.SubtractFromPlusHours" label="Haal uren van plusuren af" />
                <CqRadiobutton v-model="leavePeriodTypeRadio" :disabled="isSaving" class="radio" :value="leavePeriodTypeEnum.HoursSpreadOverWeekDays" label="Spreid contracturen over werkdagen" />
                <CqRadiobutton v-model="leavePeriodTypeRadio" :disabled="isSaving" class="radio" :value="leavePeriodTypeEnum.HoursSpreadOverFullWeeks" label="Spreid contracturen over volledige weken" />
            </div>
        </InfoGrid>
        <CqButtonBar>
            <CqButton :disabled="isSaving" label="Annuleren" @click="closeModal" />
            <CqButton :disabled="isSaving || type === null" primary label="Opslaan" icon="save" @click="save" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {CqButton, CqButtonBar, CqModal, CqRadiobutton} from '@cquential/ui';
    import CustomDateSelect from '@/components/Branding/CustomDateSelect.vue';
    import Notify from '@/components/modals/Notify.vue';
    import {LeavePeriodType, ApiClient, LeavePeriod, ApiError} from '@cquential/api';
    import Dates from '@cquential/date-utils';
    import {ResourceUtil} from '@/util/ResourceUtil';
    import InfoGrid from '@/components/Branding/InfoGrid.vue';
    import InfoHeader from '@/components/Branding/InfoHeader.vue';
    import {ResourceWithKeyedByDateObjects} from '@/types/api-object-extensions';
    import {Component, Vue, Prop} from 'vue-facing-decorator';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            CqRadiobutton,
            CustomDateSelect,
            InfoGrid,
            InfoHeader,
        },
        emits: [
            'update:preference',
            'reload:resource',
        ],
    })
    export default class LeavePeriodModal extends Vue {
        private static instance: LeavePeriodModal | null = null;

        @Prop() public rosterProposedUntil?: Date;
        @Prop({required: true}) public preferences!: Record<string, string | number>;

        private show: boolean = false;
        private isSaving: boolean = false;
        private resource: ResourceWithKeyedByDateObjects | null = null;
        private beginDate: Date = new Date();
        private endDate: Date = new Date();
        private type: LeavePeriodType | null = null;

        public get resourceDisplayName(): string {
            return this.resource ? ResourceUtil.displayName(this.resource) : '';
        }

        public get leavePeriodTypeEnum(): typeof LeavePeriodType {
            return LeavePeriodType;
        }

        public get leavePeriodTypeRadio(): number | undefined {
            return this.type ? this.type as number : undefined;
        }

        public set leavePeriodTypeRadio(value: number | undefined) {
            this.type = value ?? null;
        }

        public mounted(): void {
            LeavePeriodModal.instance = this;
        }

        public beforeUnmount(): void {
            LeavePeriodModal.instance = null;
        }

        public open(openResource: ResourceWithKeyedByDateObjects, date: Date): void {
            this.resource = openResource;
            this.beginDate = new Date(date);
            this.endDate = new Date(date);
            this.show = true;
            const savedType: number | null = 'leavePeriodTypeDefault' in this.preferences ? Number(this.preferences.leavePeriodTypeDefault) : null;
            if (savedType !== null && savedType in LeavePeriodType) {
                this.type = savedType;
            } else {
                if (savedType !== null) {
                    this.$emit('update:preference', 'leavePeriodTypeDefault', undefined);
                }
                this.type = null;
            }
        }

        public get minBeginDate(): Date | undefined {
            let minBeginDate: undefined | Date = undefined;
            if (!!this.resource && !!this.resource.salaryAcceptedUntil) {
                minBeginDate = this.resource.salaryAcceptedUntil;
            }
            if (this.rosterProposedUntil && (minBeginDate === undefined || minBeginDate.getTime() < this.rosterProposedUntil.getTime())) {
                minBeginDate = Dates.Utils.addDays(this.rosterProposedUntil, 1);
            }

            return minBeginDate;
        }

        public save(): void {
            if (this.isSaving) {
                return;
            }

            if (this.type === null) {
                throw new Error('Can only save if a type is set.');
            }

            this.$emit('update:preference', 'leavePeriodTypeDefault', this.type);

            const leavePeriod: LeavePeriod = {
                beginDate: new Date(this.beginDate),
                endDate: new Date(this.endDate),
                resourceId: this.resource!.id!,
                type: this.type,
            };

            this.isSaving = true;
            ApiClient.instance.createLeavePeriod(leavePeriod).then((id: number): void => {
                leavePeriod.id = id;
                this.closeModal();
                this.$emit('reload:resource', leavePeriod.resourceId);
            }).catch((error: ApiError): void => {
                let errorMessage: string;
                if (error.response?.data?.errors && 'error' in error.response.data.errors) {
                    const match: RegExpMatchArray | null = String(error.response.data.errors.error).match(/There are no (?:enabled )?leave kinds for (\d+)/);
                    errorMessage = match
                        ? `Er is nog geen geldige verlofregeling voor het jaar ${match[1]} voor dit type verlof`
                        : 'Er ging iets verkeerd. Probeer het opnieuw.';
                } else {
                    errorMessage = 'Er ging iets verkeerd. Probeer het opnieuw.';
                }

                /** @see Notify.createNotification() */
                // @ts-expect-error TS2339
                Notify.__vfdConstructor.createNotification({text: errorMessage, type: 'error'});
            }).finally((): void => {
                this.isSaving = false;
            });
        }

        public closeModal(): void {
            this.show = false;
        }

        public static open(resource: ResourceWithKeyedByDateObjects, date: Date): void {
            this.instance!.open(resource, date);
        }
    }
</script>

<style lang="scss" scoped>
    .radio + .radio {
        margin-top: 0.25rem;
    }
</style>
